import {useCallback, useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {useAuthenticatedSession, useUser} from "UserProvider";
import {AuthenticatedSessionState} from "auth/AuthenticatedSessionWaiter";
import {planOf} from "home/gating";
import type {CustomerBrand} from "account/AccountBrandApi";
import {accountAppColor, shouldLoadAccountAppColor} from "account/brand/accountAppColor";

/** The saved colour and the account it belongs to, so a sign-out or an account switch never shows another's. */
interface Loaded {
	accountId: string | null;
	primaryColor: string | null;
}

export interface UseAccountAppColor {
	/** The colour the app is themed with, or `null` for today's look. */
	color: string | null;
	/** Takes a brand the branding sheet just saved or loaded, so the app follows it without a reload. */
	publish: (brand: Pick<CustomerBrand, "primaryColor">) => void;
}

/**
 * Thin adapter over `GET /account/brand` for the app theme (kitecorp/transferify-ui#2935). Whether the
 * brand is asked for, and whether its colour applies, are `account/brand/accountAppColor`'s rules; this
 * only holds the answer.
 *
 * It waits for the auth listener's own verdict rather than `user.isLoggedIn`: the stored User is revived
 * from localStorage before Firebase has minted a token, so a request would go out unauthenticated.
 */
export function useAccountAppColor(): UseAccountAppColor {
	const [user] = useUser();
	const [session] = useAuthenticatedSession();
	const authenticated = session === AuthenticatedSessionState.Authenticated;
	const plan = planOf(user);
	const accountId = user.id;
	const {pathname} = useLocation();
	const load = shouldLoadAccountAppColor({authenticated, plan});
	const [loaded, setLoaded] = useState<Loaded>({accountId: null, primaryColor: null});

	useEffect(() => {
		if (!load) {
			return;
		}
		let active = true;
		// Loaded on demand, not imported: this hook is mounted by `AppProviders`, so a static import puts the
		// brand api in the eager entry chunk. Doing that moved which chunk the recipient page's styles load
		// from, and the download page's viewport lock stopped holding (its card grew past the window in
		// every download-viewport-lock spec). Only a Premium account ever reaches this line.
		import("account/AccountBrandApi")
			.then(({AccountBrandApi}) => AccountBrandApi.getBrand())
			.then(brand => {
				if (active) {
					setLoaded({accountId, primaryColor: brand.primaryColor});
				}
			})
			// Silent: without the colour the app keeps its own, which is the right look for any failure.
			.catch(() => undefined);
		return () => {
			active = false;
		};
	}, [load, accountId]);

	const publish = useCallback((brand: Pick<CustomerBrand, "primaryColor">) => {
		setLoaded({accountId, primaryColor: brand.primaryColor});
	}, [accountId]);

	const primaryColor = loaded.accountId === accountId ? loaded.primaryColor : null;
	return {color: accountAppColor({authenticated, plan, primaryColor, pathname}), publish};
}
